import { JsonSchema } from '@sagold/react-json-editor';

import {
  getPrecision,
  roundTo,
  shouldApplyFactor,
} from '../../utils/numberPrecision';

type FormData = Record<string, unknown>;

/**
 * Recursively transforms form data, applying a transformation to number fields with factor.
 */
function transformFormData(
  data: FormData | null | undefined,
  schema: JsonSchema | undefined,
  transformNumber: (value: number, factor: number) => number
): FormData | null | undefined {
  if (data == null || schema == null) {
    return data;
  }

  const properties = schema.properties;
  if (properties == null || typeof properties === 'boolean') {
    return data;
  }

  const result: FormData = { ...data };

  for (const [key, propertySchema] of Object.entries(properties)) {
    if (typeof propertySchema === 'boolean') {
      continue;
    }

    const value = data[key];
    const factor = propertySchema.options?.factor;

    // Transform number fields with factor
    if (typeof value === 'number' && shouldApplyFactor(factor)) {
      result[key] = transformNumber(value, factor);
    }

    // Recurse into nested objects
    if (
      propertySchema.type === 'object' &&
      value != null &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      result[key] = transformFormData(
        value as FormData,
        propertySchema,
        transformNumber
      );
    }

    // Recurse into arrays of objects
    if (
      propertySchema.type === 'array' &&
      Array.isArray(value) &&
      propertySchema.items != null &&
      typeof propertySchema.items !== 'boolean'
    ) {
      result[key] = value.map((item) =>
        item != null && typeof item === 'object' && !Array.isArray(item)
          ? transformFormData(
              item as FormData,
              propertySchema.items as JsonSchema,
              transformNumber
            )
          : item
      );
    }
  }

  return result;
}

/**
 * Converts form data from display values to storage values before submission.
 * Divides number fields by their factor.
 *
 * Example: User enters 12.34 (percentage), factor=100 → stored as 0.1234
 */
export function convertToStorageValues(
  data: FormData | null | undefined,
  schema: JsonSchema | undefined
): FormData | null | undefined {
  return transformFormData(data, schema, (value, factor) =>
    roundTo(value / factor, getPrecision(factor))
  );
}

/**
 * Converts backend storage values to display values for form editing.
 * Multiplies number fields by their factor.
 *
 * Example: Backend returns 0.1234, factor=100 → displays as 12.34
 */
export function convertToDisplayValues(
  data: FormData | null | undefined,
  schema: JsonSchema | undefined
): FormData | null | undefined {
  return transformFormData(data, schema, (value, factor) =>
    roundTo(value * factor, getPrecision(factor))
  );
}
